// ============================================================
// HARMONIC CHARTER — Roman Numeral Parser
// ============================================================

import type {
  Chord,
  Degree,
  ChordQuality,
  Extension,
  NormalizedAccidental,
  ParseResult,
} from '../types';
import { isValidDegree } from '../theory';
import { HOLD_TOKEN } from './nashville';

const ROMAN_TOKENS: Array<[string, Degree, 'major' | 'minor']> = [
  ['VII', 7, 'major'], ['vii', 7, 'minor'],
  ['III', 3, 'major'], ['iii', 3, 'minor'],
  ['VI',  6, 'major'], ['vi',  6, 'minor'],
  ['IV',  4, 'major'], ['iv',  4, 'minor'],
  ['II',  2, 'major'], ['ii',  2, 'minor'],
  ['V',   5, 'major'], ['v',   5, 'minor'],
  ['I',   1, 'major'], ['i',   1, 'minor'],
];

const QUALITY_SUFFIXES: Array<[string, ChordQuality]> = [
  ['mMaj7',  'minorMajor7'],
  ['m7b5',   'halfDiminished'],
  ['maj7',   'major7'],
  ['min7',   'minor7'],
  ['m7',     'minor7'],
  ['dim7',   'diminished'],
  ['dim',    'diminished'],
  ['aug',    'augmented'],
  ['sus4',   'sus4'],
  ['sus2',   'sus2'],
  ['maj9',   'major9'],
  ['m9',     'minor9'],
  ['9',      'dominant9'],
  ['7',      'dominant7'],
  ['m',      'minor'],
];

const EXTENSION_TOKENS: Array<[string, Extension]> = [
  ['add9',  'add9'],
  ['add11', 'add11'],
  ['add13', 'add13'],
  ['b9',    'b9'],
  ['#9',    '#9'],
  ['#11',   '#11'],
  ['b13',   'b13'],
  ['11',    'add11'],
  ['13',    'add13'],
];

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------

function parseAccidental(input: string, pos: number): [NormalizedAccidental, number] {
  if (input[pos] === 'b' && /[A-Za-z]/.test(input[pos + 1] ?? '')) {
    return ['flat', pos + 1];
  }
  if (input[pos] === '#') return ['sharp', pos + 1];
  return [null, pos];
}

function parseRomanNumeral(
  input: string,
  pos: number,
): [Degree | null, 'major' | 'minor', number] {
  for (const [token, degree, impliedQuality] of ROMAN_TOKENS) {
    if (input.startsWith(token, pos)) return [degree, impliedQuality, pos + token.length];
  }
  return [null, 'major', pos];
}

function parseQualitySuffix(
  input: string,
  pos: number,
  impliedQuality: 'major' | 'minor',
): [ChordQuality, number] {
  for (const [token, quality] of QUALITY_SUFFIXES) {
    if (input.startsWith(token, pos)) return [quality, pos + token.length];
  }
  return [impliedQuality, pos];
}

function parseExtensions(input: string, pos: number): [Extension[], number] {
  const extensions: Extension[] = [];
  let current = pos;
  while (current < input.length) {
    let matched = false;
    for (const [token, ext] of EXTENSION_TOKENS) {
      if (input.startsWith(token, current)) {
        extensions.push(ext);
        current += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) break;
  }
  return [extensions, current];
}

function parseSlash(input: string, pos: number): [string | null, number] {
  if (input[pos] !== '/') return [null, pos];

  let current = pos + 1;
  const char = input[current];
  if (!char) return [null, pos];

  let accidental: NormalizedAccidental = null;
  if (char === 'b' && /[1-7]/.test(input[current + 1] ?? '')) {
    accidental = 'flat';
    current++;
  } else if (char === '#') {
    accidental = 'sharp';
    current++;
  }

  const n = parseInt(input[current], 10);
  if (isValidDegree(n)) {
    return [JSON.stringify({ degree: n as Degree, accidental }), current + 1];
  }

  return [null, pos];
}

// ------------------------------------------------------------
// MAIN EXPORT
// ------------------------------------------------------------

export function parseRoman(token: string): ParseResult {
  const input = token.trim();
  if (input === '' || input === HOLD_TOKEN) {
    return { ok: false, error: 'Empty or hold token', raw: token };
  }

  let pos = 0;

  const [accidental, afterAcc] = parseAccidental(input, pos);
  pos = afterAcc;

  const [degree, impliedQuality, afterRoman] = parseRomanNumeral(input, pos);
  if (degree === null) {
    return {
      ok: false,
      error: `Expected Roman numeral, got '${input.slice(pos) || 'end'}'`,
      raw: token,
    };
  }
  pos = afterRoman;

  const [quality, afterQuality] = parseQualitySuffix(input, pos, impliedQuality);
  pos = afterQuality;

  const [extensions, afterExt] = parseExtensions(input, pos);
  pos = afterExt;

  const [slash, afterSlash] = parseSlash(input, pos);
  pos = afterSlash;

  if (pos < input.length) {
    return {
      ok: false,
      error: `Unexpected characters '${input.slice(pos)}' after chord`,
      raw: token,
    };
  }

  const chord: Chord = {
    degree,
    accidental,
    quality,
    extensions,
    slash,
    position: 0,
    borrowed: false,
  };

  return { ok: true, chord };
}

export function looksLikeRoman(token: string): boolean {
  return /^[b#]?[IiVv]/.test(token.trim());
}